import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, FileText, Truck } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { useCart, fmt } from "../context/CartContext";
import { CommerceGate } from "../components/cart/CommerceGate";
import { quoteHandoff } from "./CartPage";

const API = process.env.REACT_APP_BACKEND_URL;
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];
const COUNTRIES = [["US", "United States"], ["CA", "Canada"], ["MX", "Mexico"], ["GB", "United Kingdom"], ["AU", "Australia"], ["OTHER", "Other country"]];

const inputCls = "w-full bg-[#0A0A0A] border border-white/20 px-4 py-3 text-white focus:border-signal-red focus:outline-none focus:ring-2 focus:ring-signal-red/20 transition-all";

// FedEx commit date "2026-10-08T17:00:00" → "Est. delivery Thu, Oct 8" (date part only; never computed by us)
export const fmtTransit = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso || "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `Est. delivery ${d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}`;
};

const Field = ({ id, label, children, required = true, className = "" }) => (
  <div className={className}>
    <label htmlFor={id} className="block text-gray-400 text-sm mb-2">{label}{required && " *"}</label>
    {children}
  </div>
);

const CheckoutPage = () => (
  <CommerceGate title="Checkout"><CheckoutContent /></CommerceGate>
);

const CheckoutContent = () => {
  const { lines, items, subtotal } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "", lift_type: "", line1: "", line2: "", city: "", state: "", postal_code: "", country: "US", residential: false });
  const [shipping, setShipping] = useState(null);
  const [rateId, setRateId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [payUrl, setPayUrl] = useState("");

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    setShipping(null);
    setRateId("");
  };

  const address = () => ({ line1: form.line1, line2: form.line2, city: form.city, state: form.state, postal_code: form.postal_code, country: form.country === "OTHER" ? "XX" : form.country, residential: form.residential });
  const customerValid = form.first_name && form.last_name && form.email && form.phone;
  const addressValid = form.line1 && form.city && form.state && form.postal_code && form.country;
  const isUS = form.country === "US";

  const getRates = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/shipping/rates`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ items, address: address() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Unable to calculate shipping");
      setShipping(data);
      if (data.status === "ok" && data.rates.length === 1) setRateId(data.rates[0].rate_id);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const toQuote = (reason) => {
    quoteHandoff(lines, { reason, country: form.country, customer: form });
    navigate("/#quote");
  };

  const pay = async () => {
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/checkout/session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items, customer: { first_name: form.first_name, last_name: form.last_name, company: form.company, email: form.email, phone: form.phone, lift_type: form.lift_type }, address: address(), quote_id: shipping.quote_id, rate_id: rateId, origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Unable to start checkout");
      // Stripe Checkout cannot run inside an iframe (e.g. the Emergent preview panel): open it top-level there.
      if (window.self !== window.top) {
        const win = window.open(data.checkout_url, "_blank");
        if (win) win.opener = null;
        setPayUrl(data.checkout_url);
        setBusy(false);
        if (!win) setError("Your browser blocked the payment window. Use the button below to open the secure Stripe payment page.");
        return;
      }
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  if (lines.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-28 text-center px-6">
        <p className="text-gray-400 mb-6" data-testid="checkout-empty">Your cart is empty.</p>
        <Link to="/#products" className="inline-flex px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm">Browse Products</Link>
      </div>
    );
  }

  const selectedRate = shipping?.status === "ok" ? shipping.rates.find((r) => r.rate_id === rateId) : null;

  return (
    <>
      <SEOHead title="Checkout | Highwall Lift" description="Secure Highwall Lift checkout." canonicalPath="/checkout" />
      <div className="min-h-screen bg-[#0A0A0A] pt-28 pb-24">
        <div className="max-w-6xl mx-auto px-6 sm:px-8">
          <p className="font-mono text-signal-red uppercase tracking-[0.2em] text-xs font-bold mb-3">Highwall Lift</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight uppercase text-white mb-8" data-testid="checkout-title">Checkout</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <form onSubmit={getRates} className="lg:col-span-2 space-y-8" data-testid="checkout-form">
              <section className="bg-[#141414] border border-white/10 p-6">
                <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">Contact & Company</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field id="first_name" label="First Name"><input id="first_name" name="first_name" required value={form.first_name} onChange={set} className={inputCls} data-testid="checkout-first-name" /></Field>
                  <Field id="last_name" label="Last Name"><input id="last_name" name="last_name" required value={form.last_name} onChange={set} className={inputCls} data-testid="checkout-last-name" /></Field>
                  <Field id="company" label="Company" required={false}><input id="company" name="company" value={form.company} onChange={set} className={inputCls} data-testid="checkout-company" /></Field>
                  <Field id="email" label="Email"><input id="email" name="email" type="email" required value={form.email} onChange={set} className={inputCls} data-testid="checkout-email" /></Field>
                  <Field id="phone" label="Phone"><input id="phone" name="phone" type="tel" required value={form.phone} onChange={set} className={inputCls} data-testid="checkout-phone" /></Field>
                  <Field id="lift_type" label="What type of lift are you currently using?" required={false}>
                    <select id="lift_type" name="lift_type" value={form.lift_type} onChange={set} className={inputCls} data-testid="checkout-lift-type">
                      <option value="">Select (optional)</option><option>Scissor Lift</option><option>Boom Lift</option><option>Both</option><option>Not Sure</option>
                    </select>
                  </Field>
                </div>
              </section>

              <section className="bg-[#141414] border border-white/10 p-6">
                <h2 className="font-heading text-xl font-bold text-white uppercase mb-6">Shipping Address</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field id="country" label="Country" className="md:col-span-2">
                    <select id="country" name="country" value={form.country} onChange={set} className={inputCls} data-testid="checkout-country">
                      {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
                    </select>
                  </Field>
                  {!isUS && (
                    <div className="md:col-span-2 p-4 border border-signal-red/40 bg-signal-red/10 text-gray-200" data-testid="checkout-international-notice">
                      <p className="font-bold text-white mb-1">International orders are handled by quote.</p>
                      <p className="text-sm mb-3">We ship U.S. &amp; international. For destinations outside the U.S. we confirm freight, duties and taxes before payment.</p>
                      <button type="button" onClick={() => toQuote("international")} className="inline-flex items-center gap-2 px-5 py-3 bg-signal-red text-white font-bold uppercase text-sm" data-testid="checkout-international-quote">
                        <FileText className="w-4 h-4" /> Request International Quote
                      </button>
                    </div>
                  )}
                  <Field id="line1" label="Street Address" className="md:col-span-2"><input id="line1" name="line1" required value={form.line1} onChange={set} className={inputCls} data-testid="checkout-line1" /></Field>
                  <Field id="line2" label="Suite / Unit" required={false} className="md:col-span-2"><input id="line2" name="line2" value={form.line2} onChange={set} className={inputCls} data-testid="checkout-line2" /></Field>
                  <Field id="city" label="City"><input id="city" name="city" required value={form.city} onChange={set} className={inputCls} data-testid="checkout-city" /></Field>
                  <Field id="state" label={isUS ? "State" : "State / Province"}>
                    {isUS ? (
                      <select id="state" name="state" required value={form.state} onChange={set} className={inputCls} data-testid="checkout-state">
                        <option value="">Select</option>{US_STATES.map((s) => <option key={s}>{s}</option>)}
                      </select>
                    ) : <input id="state" name="state" required value={form.state} onChange={set} className={inputCls} data-testid="checkout-state" />}
                  </Field>
                  <Field id="postal_code" label={isUS ? "ZIP" : "Postal Code"}><input id="postal_code" name="postal_code" required value={form.postal_code} onChange={set} className={inputCls} data-testid="checkout-zip" /></Field>
                  <label className="flex items-center gap-3 text-gray-300 md:pt-8">
                    <input type="checkbox" name="residential" checked={form.residential} onChange={set} className="w-4 h-4 accent-[#FF3333]" data-testid="checkout-residential" />
                    Residential address
                  </label>
                </div>
                {isUS && (
                  <button type="submit" disabled={busy || !customerValid || !addressValid} className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-white/10 border border-white/20 text-white font-bold uppercase text-sm tracking-wide hover:bg-white/20 disabled:opacity-40 transition-colors" data-testid="checkout-calculate-shipping">
                    <Truck className="w-4 h-4" /> {busy ? "Calculating…" : "Calculate Shipping"}
                  </button>
                )}
              </section>

              {shipping && shipping.status === "ok" && (
                <section className="bg-[#141414] border border-white/10 p-6" data-testid="checkout-shipping-options">
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-4">Shipping Method</h2>
                  <ul className="space-y-3">
                    {shipping.rates.map((r) => (
                      <li key={r.rate_id}>
                        <label className="flex items-center justify-between gap-4 p-4 border border-white/10 hover:border-signal-red/60 cursor-pointer">
                          <span className="flex items-center gap-3 text-gray-200"><input type="radio" name="rate" checked={rateId === r.rate_id} onChange={() => setRateId(r.rate_id)} className="accent-[#FF3333]" data-testid={`checkout-rate-${r.rate_id}`} />{r.service}{r.transit && <span className="text-gray-500 text-xs" data-testid={`checkout-rate-${r.rate_id}-transit`}>({fmtTransit(r.transit)})</span>}</span>
                          <span className="font-heading font-black text-white">{fmt(r.amount_cents / 100)}</span>
                        </label>
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-500 text-xs mt-3">Freight charged at carrier rate. No handling fee. Tax is calculated on the secure payment page.</p>
                </section>
              )}

              {shipping && shipping.status !== "ok" && (
                <section className="bg-[#141414] border border-signal-red/40 p-6" data-testid={`checkout-shipping-${shipping.status}`}>
                  <h2 className="font-heading text-xl font-bold text-white uppercase mb-2">{shipping.status === "international_quote" ? "International Quote Required" : "Freight Quote Required"}</h2>
                  <p className="text-gray-300 mb-4">{shipping.message}</p>
                  <button type="button" onClick={() => toQuote(shipping.status)} className="inline-flex items-center gap-2 px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm" data-testid="checkout-request-freight-quote">
                    <FileText className="w-4 h-4" /> {shipping.status === "international_quote" ? "Request International Quote" : "Request a Freight Quote"}
                  </button>
                </section>
              )}
              {error && <p className="text-signal-red" data-testid="checkout-error">{error}</p>}
            </form>

            <aside className="bg-[#141414] border border-white/10 p-6 h-fit" data-testid="checkout-summary">
              <h2 className="font-heading text-xl font-bold text-white uppercase mb-4">Order Summary</h2>
              <ul className="divide-y divide-white/10 mb-4">
                {lines.map((l) => (
                  <li key={l.sku} className="py-3 flex justify-between gap-3 text-sm" data-testid={`checkout-line-${l.sku}`}>
                    <span className="text-gray-300">{l.name} <span className="text-gray-500">({l.model}) × {l.quantity}</span></span>
                    <span className="text-white font-bold">{fmt(l.lineTotal)}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-300"><span>Subtotal</span><span data-testid="checkout-subtotal">{fmt(subtotal)}</span></div>
                <div className="flex justify-between text-gray-300"><span>Shipping</span><span data-testid="checkout-shipping-amount">{selectedRate ? fmt(selectedRate.amount_cents / 100) : "—"}</span></div>
                <div className="flex justify-between text-gray-300"><span>Tax</span><span>Calculated at payment</span></div>
                <div className="flex justify-between text-white font-heading text-xl font-black pt-3 border-t border-white/10"><span>Total before tax</span><span data-testid="checkout-total">{selectedRate ? fmt(subtotal + selectedRate.amount_cents / 100) : "—"}</span></div>
              </div>
              <button type="button" onClick={pay} disabled={!selectedRate || busy} className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 bg-signal-red text-white font-bold uppercase tracking-wide hover:bg-signal-red/90 disabled:opacity-40 transition-colors" data-testid="checkout-pay-button">
                <Lock className="w-4 h-4" /> Continue to Secure Payment
              </button>
              <p className="text-gray-500 text-xs mt-3">Payment is processed by Stripe. Highwall Lift never sees or stores your card details.</p>
              <p className="text-gray-500 text-xs mt-2" data-testid="checkout-policy-consent">
                By continuing you agree to our <Link to="/policies/terms" className="underline hover:text-white">Terms of Sale</Link>, <Link to="/policies/shipping" className="underline hover:text-white">Shipping Policy</Link>, <Link to="/policies/returns" className="underline hover:text-white">Returns &amp; Refunds Policy</Link> and <Link to="/policies/privacy" className="underline hover:text-white">Privacy Policy</Link>.
              </p>
              {payUrl && (
                <a href={payUrl} target="_blank" rel="noopener noreferrer" className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 border border-signal-red text-white font-bold uppercase text-sm tracking-wide hover:bg-signal-red/10 transition-colors" data-testid="checkout-open-payment-link">
                  <Lock className="w-4 h-4" /> Open Secure Payment Page
                </a>
              )}
              <Link to="/cart" className="block text-center mt-4 text-gray-400 hover:text-white text-sm uppercase tracking-wide" data-testid="checkout-back-to-cart">Back to Cart</Link>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default CheckoutPage;
